import * as Sentry from '@sentry/react'
import OneSignal from 'react-onesignal'
import { isAppShell, isCordova } from '../util'

// Web push through OneSignal (web SDK v16): desktop browsers, Android Chrome and, from iOS 16.4 on,
// the app added to the iPhone home screen. The native apps (Cordova) keep their own plugin.

export type WebPushState =
  | 'unavailable' // not set up here (native app, local dev without push)
  | 'failed' // the SDK did not start (yet)
  | 'unsupported' // browser without web push, e.g. iOS older than 16.4
  | 'needsHomeScreen' // iPhone/iPad in Safari: push only works in the app from the home screen
  | 'denied' // permission refused in the browser settings
  | 'off'
  | 'on'

const DEV_APP_ID = '0e2c2191-5dc1-4cac-a6cb-e852b8fedf30'
const INIT_TIMEOUT_MS = 15000

// null while the SDK starts, then whether it started
let started: boolean | null = false
let ready: Promise<boolean> = Promise.resolve(false)
let gaveUp: Promise<void> = Promise.resolve()
const listeners = new Set<() => void>()

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms) })
const notify = () => listeners.forEach((listener) => listener())

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches
  || window.matchMedia?.('(display-mode: fullscreen)').matches
  || (navigator as any).standalone === true

function enabledHere() {
  return !isCordova() && ['production', 'development'].includes(process.env.NODE_ENV ?? '')
}

function report(error: unknown) {
  // eslint-disable-next-line no-console
  console.warn('web push not available', error)
  Sentry.captureMessage('Web push did not start', {
    level: 'warning',
    tags: { area: 'web-push' },
    extra: {
      error: String(error), userAgent: navigator.userAgent, standalone: isStandalone(), ios: isIos()
    }
  })
}

export function initWebPush() {
  if (!enabledHere() || !OneSignal.Notifications.isPushSupported()) return
  const production = process.env.NODE_ENV === 'production'
  started = null
  ready = OneSignal.init({
    appId: production ? process.env.REACT_APP_ONE_SIGNAL_APP_ID as string : DEV_APP_ID,
    allowLocalhostAsSecureOrigin: !production,
    // the worker the earlier SDK registered, so existing subscriptions stay. In production it is the app's
    // own worker (src/serviceWorker/OneSignalSDKWorker.ts built to this address), the dev server serves the plain push worker
    serviceWorkerPath: production ? 'OneSignalSDKWorker.js' : 'OneSignalSDKWorkerDev.js',
    serviceWorkerParam: { scope: '/' },
    autoResubscribe: true
  }).then(() => true).catch((error) => {
    report(error)
    return false
  }).then((ok) => {
    started = ok
    notify()
    return ok
  })
  // a slow start shows as failed, and recovers when the SDK answers after all
  gaveUp = wait(INIT_TIMEOUT_MS).then(() => {
    if (started !== null) return
    report(new Error(`no answer after ${INIT_TIMEOUT_MS} ms`))
    notify()
  })
}

// pushes are addressed by the user id (external id), see backend OneSignalHelper
export async function loginWebPush(userId: number) {
  if (await ready) await OneSignal.login(userId.toString()).catch(() => undefined)
}

// a shared device must not get the pushes of the person before
export async function logoutWebPush() {
  if (await ready) await OneSignal.logout().catch(() => undefined)
}

export async function webPushState(): Promise<WebPushState> {
  if (!enabledHere()) return 'unavailable'
  // the iOS store app has no web push (WKWebView), it gets native push; never send it to the home screen
  if (isAppShell() && isIos()) return 'unavailable'
  if (isIos() && !isStandalone()) return 'needsHomeScreen'
  if (!OneSignal.Notifications.isPushSupported()) return 'unsupported'
  await Promise.race([ready, gaveUp])
  if (!started) return 'failed'
  if (OneSignal.Notifications.permissionNative === 'denied') return 'denied'
  return OneSignal.Notifications.permission && OneSignal.User.PushSubscription.optedIn ? 'on' : 'off'
}

// must run in a tap or click: iOS only asks for the permission then
export async function enableWebPush() {
  if (!await ready) return webPushState()
  if (!OneSignal.Notifications.permission) await OneSignal.Notifications.requestPermission()
  if (OneSignal.Notifications.permission && !OneSignal.User.PushSubscription.optedIn) {
    await OneSignal.User.PushSubscription.optIn()
  }
  // OneSignal confirms the subscription a moment after the permission
  for (let i = 0; i < 20 && OneSignal.Notifications.permission && !OneSignal.User.PushSubscription.optedIn; i += 1) {
    await wait(250)
  }
  return webPushState()
}

export async function disableWebPush() {
  if (await ready) await OneSignal.User.PushSubscription.optOut()
  return webPushState()
}

export function onWebPushChange(listener: () => void) {
  listeners.add(listener)
  ready.then((ok) => {
    if (!ok || !listeners.has(listener)) return
    OneSignal.Notifications.addEventListener('permissionChange', listener)
    OneSignal.User.PushSubscription.addEventListener('change', listener)
  })
  return () => {
    listeners.delete(listener)
    ready.then((ok) => {
      if (!ok) return
      OneSignal.Notifications.removeEventListener('permissionChange', listener)
      OneSignal.User.PushSubscription.removeEventListener('change', listener)
    })
  }
}
